import React, { useEffect, useMemo, useState } from 'react';
import { makeStyles, withStyles } from '@material-ui/core/styles';
import { Paper, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, CircularProgress } from '@material-ui/core';
import { formatters } from '../../../../Core/Controls';

const useStyles = makeStyles({
    root: {
        width: '100%',
        padding: '10px 0px'
    },
    container: {
        height: 'calc(100vh - 235px)'
    }
});

const StyledTableCell = withStyles((theme) => ({
    head: {
        padding: 4,
        fontWeight: 'bold',
        fontSize: 12,
        border: '1px solid rgba(0,0,0,0.1)'
    },
    body: {
        fontSize: 14,
        padding: 3,
        border: '1px solid rgba(0,0,0,0.1)'
    }
}))(TableCell);

const StyledTableRow = withStyles((theme) => ({
    root: {
        '&:nth-of-type(odd)': {
            backgroundColor: theme.palette.action.hover
        }
    }
}))(TableRow);

export default function ReportListTable({ columns = [], rows = [], loadingData = false }) {
    const classes = useStyles();

    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(50);
    const [searchTerm, setSearchTerm] = useState('');
    const [filteredData, setFilteredData] = useState([]);

    // Initialize data safely
    useEffect(() => {
        if (Array.isArray(rows) && rows.length > 0) {
            setFilteredData(rows);
            setPage(0);
        } else {
            setFilteredData([]);
        }
    }, [rows]);

    const handleChangePage = (_, newPage) => setPage(newPage);

    const handleChangeRowsPerPage = (event) => {
        const newRowsPerPage = parseInt(event.target.value, 10) || 10;
        setRowsPerPage(newRowsPerPage);
        setPage(0);
    };

    const handleFilter = (e) => {
        const value = e.target.value.toLowerCase();
        setSearchTerm(value);

        if (!value.trim()) {
            setFilteredData(rows);
            setPage(0);
            return;
        }

        try {
            const result = rows.filter((item) =>
                Object.values(item || {}).some((val) =>
                    String(val ?? '')
                        .toLowerCase()
                        .includes(value)
                )
            );
            setFilteredData(result);
            setPage(0);
        } catch (error) {
            console.error('Error filtering rows:', error);
            setFilteredData(rows);
        }
    };

    // 🔹 Use useMemo to avoid recalculating on every render
    const paginatedData = useMemo(() => {
        const start = page * rowsPerPage;
        const end = start + rowsPerPage;
        return filteredData.slice(start, end);
    }, [filteredData, page, rowsPerPage]);

    // 🔹 Guard: No columns or rows
    if (!columns.length) {
        return (
            <div
                style={{
                    padding: 20,
                    textAlign: 'center',
                    height: 300,
                    verticalAlign: 'center',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center'
                }}
            >
                <span>Please Wait while fetching data</span>
            </div>
        );
    }

    return (
        <div className={classes.root}>
            {loadingData ? (
                <div style={{ display: 'flex', justifyContent: 'center', padding: 20 }}>
                    <CircularProgress />
                </div>
            ) : (
                <>
                    {/* Search Box */}
                    <input
                        type="text"
                        placeholder="Search..."
                        value={searchTerm}
                        onChange={handleFilter}
                        style={{
                            padding: '8px',
                            width: '300px',
                            border: '1px solid gray',
                            borderRadius: '4px',
                            marginBottom: '10px'
                        }}
                    />

                    {/* Table */}
                    <TableContainer className={classes.container} component={Paper}>
                        <Table stickyHeader aria-label="report table">
                            <TableHead>
                                <StyledTableRow>
                                    {columns.map((col) => (
                                        <StyledTableCell key={col.id || col.name} align={col.align || 'left'} style={{ minWidth: col.width || 90 }}>
                                            {col.name}
                                        </StyledTableCell>
                                    ))}
                                </StyledTableRow>
                            </TableHead>

                            <TableBody>
                                {paginatedData.length > 0 ? (
                                    paginatedData.map((row, index) => (
                                        <StyledTableRow hover tabIndex={-1} key={row.id || index}>
                                            {columns.map((column) => {
                                                const value = row[column.key];
                                                const displayValue = column.type === 'D' ? formatters?.DateTimeFormatter?.(value) ?? '' : value ?? '';
                                                return (
                                                    <StyledTableCell key={column.id || column.key} align={column.align || 'left'}>
                                                        {displayValue}
                                                    </StyledTableCell>
                                                );
                                            })}
                                        </StyledTableRow>
                                    ))
                                ) : (
                                    <StyledTableRow>
                                        <StyledTableCell colSpan={columns.length} align="center" style={{ fontStyle: 'italic', color: '#777' }}>
                                            No records found
                                        </StyledTableCell>
                                    </StyledTableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    {/* Pagination */}
                    <TablePagination
                        component="div"
                        count={filteredData.length}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onChangePage={handleChangePage} // ✅ MUI v4 syntax
                        onChangeRowsPerPage={handleChangeRowsPerPage} // ✅ MUI v4 syntax
                        rowsPerPageOptions={[10, 25, 50, 100]} // Set [] to hide dropdown
                    />
                </>
            )}
        </div>
    );
}
